import { StandaloneButtonProps } from '@/components/element/Button';
import { StandaloneIconProps } from '@/components/element/Icon';
import { StandaloneNavigation, StandaloneNavigationProps } from '@/components/layout/Navigation';
import { Navigation } from '@/components/layout/Navigation/Navigation';
import SvenskdamLogo from '@/svenskdam/public/image/svenskdam-logo.svg';
import { LabradorComponent, StandaloneComponent } from '@/types/component';
import GoogleNewsIcon from 'base/public/image/google-news.svg';
import { useMobileAppState } from 'lib/hooks/useMobileAppState';
import { showOneTrustConsent } from 'lib/utils/oneTrust';
import { standaloneComponent } from 'modules/dynamic-components';

const Button = standaloneComponent<StandaloneButtonProps>('ElementButton');
const Icon = standaloneComponent<StandaloneIconProps>('ElementIcon');

const logo: StandaloneNavigationProps['logo'] = <SvenskdamLogo />;

const menu: StandaloneNavigationProps['menu'] = (
  <>
    <Navigation.Link href="/" content="Start" />
    <Navigation.Link href="/kungligt" content="Kungligt" />
    <Navigation.Link href="/noje" content="Kändis" />
    <Navigation.Link href="/nobel" content="Nobel" />
    <Navigation.Link href="/video/" content="TV" />
  </>
);

const controls: StandaloneNavigationProps['controls'] = (
  <button type="button" onClick={showOneTrustConsent}>
    <Icon name="cookie" options={{ className: 'text-white', size: 'medium' }} />
  </button>
);

const drawerLinkProps = { options: { colors: 'drawer', size: 'drawer', variant: 'drawerPrimary' } };
const drawer: StandaloneNavigationProps['drawer'] = {
  menu: (
    <>
      <Navigation.Menu size="drawer" variant="drawer">
        <Navigation.Link href="/kungligt" {...drawerLinkProps} content="Kungligt" />

        <Navigation.Link href="/noje" {...drawerLinkProps} content="Kändis" />

        <Navigation.Link href="/kungliga-modebloggen" {...drawerLinkProps} content="Kungligt mode" />

        <Navigation.Link href="/nobel" {...drawerLinkProps} content="Nobel" />

        <Navigation.Link href="/video/" {...drawerLinkProps} content="TV" />

        <Navigation.Link href="/vimmel" {...drawerLinkProps} content="Vimmel" />
      </Navigation.Menu>

      <Navigation.Menu size="drawer" variant="drawer">
        <Navigation.Link
          href="https://prenumerera.se/tidningar/svensk-damtidning/?utm_source=svenskdam.se&utm_medium=menu&utm_campaign=svenskdamtidning"
          {...drawerLinkProps}
          content="Prenumerera"
        />

        <Navigation.Link href="/nyhetsbrev" {...drawerLinkProps} content="Nyhetsbrev" />

        <Navigation.Link href="/alla-amnen" {...drawerLinkProps} content="Alla ämnen" />

        <Navigation.Link href="/svensk-damtidnings-lasarpanel" {...drawerLinkProps} content="Läsarpanel" />

        <Navigation.Link href="/vara-skribenter" {...drawerLinkProps} content="Våra skribenter" />
        <Navigation.Link href="/creative-studio" {...drawerLinkProps} content="Creative studio" />
      </Navigation.Menu>

      <Navigation.Menu size="drawer" variant="drawer">
        <Navigation.Link href="/kontaktaoss" {...drawerLinkProps} content="Kontakta oss" />

        <Navigation.Link
          href="/om-oss"
          options={{ ...drawerLinkProps.options, variant: 'drawerSecondary' }}
          content="Om oss"
        />

        <Navigation.Link
          href="https://svenskdamtidning.ocast.com/"
          options={{ ...drawerLinkProps.options, variant: 'drawerSecondary' }}
          content="Annonsera"
        />

        <Navigation.Link
          href="/om-cookies"
          options={{ ...drawerLinkProps.options, variant: 'drawerSecondary' }}
          content="Om Cookies"
        />

        <Navigation.Link
          href="#"
          options={{
            ...drawerLinkProps.options,
            variant: 'drawerSecondary',
            onClick: (e) => {
              showOneTrustConsent();
              e.preventDefault();
            },
          }}
          content="Hantera preferenser"
        />

        <Navigation.Link
          href="https://www.aller.se/ai-policy"
          options={{ ...drawerLinkProps.options, variant: 'drawerSecondary' }}
          content="Aller Medias AI-policy"
        />
      </Navigation.Menu>
    </>
  ),
  toggle: (
    <Navigation.Drawer.Toggle
      options={{
        $segment: {
          className: '!bg-white',
        },
      }}
    />
  ),
};

const soMe: StandaloneNavigationProps['soMe'] = (
  <Navigation.SoMe.Group>
    <Navigation.SoMe.Headline>Följ Svensk Dam</Navigation.SoMe.Headline>

    <Navigation.SoMe
      href="https://www.facebook.com/svenskdamtidning/?locale=sv_SE"
      name="facebook"
      aria-label="Länk till Facebook"
    />
    <Navigation.SoMe
      href="https://www.instagram.com/svenskdamtidning/"
      name="instagram"
      aria-label="Länk till Instagram"
    />
    <Navigation.SoMe href="https://twitter.com/svenskdam" name="twitter" aria-label="Länk till Twitter" />
    <Button
      link={{
        href: 'https://news.google.com/publications/CAAiEMOO7LLrA0PM4gJfbc3NYiYqFAgKIhDDjuyy6wNDzOICX23NzWIm?hl=sv&gl=SE&ceid=SE%3Asv',
        target: '_blank',
      }}
      options={{
        size: 'small',
        'aria-label': 'Länk till Google nyheter',
      }}
      content={
        <>
          <GoogleNewsIcon className="w-6" />
          <span className="whitespace-nowrap">Google nyheter</span>
        </>
      }
    />
  </Navigation.SoMe.Group>
);

export const SvenskdamStandaloneNavigation: StandaloneComponent<StandaloneNavigationProps> = () => {
  const { isMobileApp } = useMobileAppState();

  if (isMobileApp) {
    return null;
  }

  return (
    <StandaloneNavigation
      options={{
        className: 'hideInMobileApp',
        $search: {
          $submit: {
            options: {
              size: 'medium',
              colors: 'secondary',
            },
          },
        },
      }}
      {...{ logo, menu, controls, drawer, soMe }}
    />
  );
};

export const standalone = SvenskdamStandaloneNavigation;

const SvenskdamLabradorNavigation: LabradorComponent = () => <SvenskdamStandaloneNavigation />;

export default SvenskdamLabradorNavigation;
